import React, { useState } from 'react';
import moment from 'moment';
import axios from 'axios';
import { ArrowRightEndOnRectangleIcon, ArrowRightStartOnRectangleIcon } from '@heroicons/react/20/solid';
import { PlayIcon, ClockIcon, ArrowDownTrayIcon, ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';
import { baseAPI } from '../../../../config';
import { formatDuration } from '../../utils/interactionUtils';
import { sanitizeEmailHtml } from '../../../../utils/formatters';
import { getInteractionCategory } from './utils/getInteractionCategory';
import { translateInteractionType } from './utils/translateInteractionType';
import { colorClasses } from './utils/colorClasses';
import { truncateText } from './utils/textUtils';
import userStore from '../../../../store/userStore';

const InteractionItem = React.memo(({ interactionItem, isLast = false, expedientId }) => {
  // El nombre de la línea es nuestro («Samsung Nuevo»): al cliente le basta el número.
  const esAdmin = userStore(state => state.user?.role) === 'admin';
  const getDirectionIcon = () => {
    if (interactionItem.direction === 'outgoing') {
      return <ArrowRightStartOnRectangleIcon className='shrink-0 size-4' />;
    } else {
      return <ArrowRightEndOnRectangleIcon className='shrink-0 size-4' />;
    }
  };

  const [isFetchingRecording, setIsFetchingRecording] = useState(false);
  const [isLoadingRecording, setIsLoadingRecording] = useState(false);
  const [isEmailExpanded, setIsEmailExpanded] = useState(false);

  const isEmail = interactionItem.type === 'email_sent' || interactionItem.type === 'email_response';

  const handlePlayRecording = async () => {
    const s3Key = interactionItem.details?.recording_s3_key;
    const recordingUrl = interactionItem.details?.recording_url;

    // If we have the S3 key, regenerate a fresh signed URL
    if (s3Key) {
      setIsLoadingRecording(true);
      try {
        const token = localStorage.getItem('token');
        const response = await axios.post(
          `${baseAPI}/documents/get-signed-url`,
          { s3Key },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (response.data?.url) {
          window.open(response.data.url, '_blank');
        } else {
          toast.error('No se pudo obtener la URL de la grabación');
        }
      } catch (error) {
        console.error('Error getting signed URL:', error);
        toast.error('Error al obtener la grabación');
      } finally {
        setIsLoadingRecording(false);
      }
    } else if (recordingUrl) {
      // Fallback to stored URL if no S3 key (may be expired)
      window.open(recordingUrl, '_blank');
    }
  };

  const handleGetAircallRecording = async () => {
    const callId = interactionItem.details?.call_id;
    if (!callId) {
      toast.error('No se encontró el ID de la llamada');
      return;
    }

    setIsFetchingRecording(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${baseAPI}/actions/get-aircall-recording`,
        { callId },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data?.recordingUrl) {
        // Open the recording URL in a new tab
        window.open(response.data.recordingUrl, '_blank');
        toast.success('Grabación obtenida de Aircall');
      } else {
        toast.error('No se encontró grabación disponible en Aircall');
      }
    } catch (error) {
      const errorMessage =
        error.response?.data?.error ||
        error.response?.data?.message ||
        error.message ||
        'Error al obtener la grabación';
      toast.error(errorMessage);
      console.error('Error fetching Aircall recording:', error);
    } finally {
      setIsFetchingRecording(false);
    }
  };

  /**
   * Cualquier interacción de llamada, venga del proveedor que venga.
   *
   * 🔴 Antes esto era `type === 'call_ended'` a secas, y por eso **las llamadas
   * de Telnyx no ofrecían dónde pulsar para escucharlas** aunque tuvieran la
   * grabación guardada y bien enlazada.
   *
   * Aircall escribe DOS filas por llamada —`call_answered` al descolgar y
   * `call_ended` al terminar— y cuelga el audio de la segunda. Telnyx escribe
   * UNA sola, a propósito, con el tipo que describe qué pasó: `call_answered`,
   * `call_made` o `call_voicemail`.
   *
   * El tipo de interacción debe decir **qué pasó**, no servir de bandera para
   * que la interfaz pinte un botón. Lo que decide si hay algo que escuchar es
   * que haya grabación, no cómo se llame la fila.
   */
  const isCall = String(interactionItem.type || '').startsWith('call');

  const hasRecording =
    isCall && (interactionItem.details?.recording_s3_key || interactionItem.details?.recording_url);

  // ⚠️ El botón de "obtener grabación de Aircall" sigue atado a `call_ended`: va
  // contra su API con el `call_id` de Aircall, y ofrecerlo en una llamada de
  // Telnyx sería un botón que sólo puede fallar.
  const isCallEnded = interactionItem.type === 'call_ended';
  const hasCallId = isCallEnded && interactionItem.details?.call_id;
  const canFetchRecording = isCallEnded && !hasRecording && hasCallId;

  const duration =
    isCall && interactionItem.details?.duration ? formatDuration(interactionItem.details.duration) : null;

  const { icon: CategoryIcon, color } = getInteractionCategory(interactionItem.type);
  const translatedType = translateInteractionType(interactionItem.type);
  const colors = colorClasses[color] || colorClasses.gray;

  return (
    <div className='grid grid-cols-[100px_32px_1fr] gap-4 items-start py-1'>
      {/* Column 1: Date and Time */}
      <div className='text-end pt-2'>
        <div className='text-xs text-slate-500 font-medium'>{moment(interactionItem.date).format('DD/MM/YYYY')}</div>
        <div className='text-xs text-slate-400 mt-0.5'>{moment(interactionItem.date).format('HH:mm')}h</div>
      </div>

      {/* Column 2: Timeline Dots and Lines */}
      <div className='relative flex justify-center'>
        {/* Continuous vertical line - only if not last */}
        {!isLast && <div className='absolute bottom-0 left-1/2 transform -translate-x-px w-0.5 bg-slate-200'></div>}
        {/* Dot with icon */}
        <div className='relative z-10 flex justify-center items-center'>
          <div
            className={`mt-2 flex items-center justify-center w-8 h-8 rounded-full border-2 ${colors.bg} ${colors.border}`}
          >
            <CategoryIcon className={`w-3 h-3 ${colors.text}`} />
          </div>
        </div>
      </div>

      {/* Column 3: Description Card */}
      <div className='pt-0.5'>
        <div className={`rounded-lg border p-3 ${colors.bg} ${colors.border}`}>
          {/* Header with category badge and type */}
          <div className='flex items-start justify-between gap-2'>
            <div className='flex items-center gap-2 flex-1 min-w-0'>
              {getDirectionIcon()}
              <h3 className='text-sm font-semibold text-slate-800 truncate'>{translatedType}</h3>
            </div>
          </div>

          {/* Phone numbers info for calls - Aligned to the right in a box */}
          {interactionItem.type?.includes('call') && (
            <div className='px-2.5 py-0.5 mt-2 bg-slate-50/80 border border-slate-200 rounded-lg flex items-center gap-1.5 text-xs text-slate-600 shrink-0'>
              {interactionItem.direction === 'outgoing' ? (
                <>
                  <span className='font-semibold'>
                    {interactionItem.details?.aircall_phone_name && (
                      <span className='mr-1'>{truncateText(interactionItem.details.aircall_phone_name)}</span>
                    )}
                    <span className='font-mono'>
                      {truncateText(interactionItem.details?.aircall_phone_number || 'N/A')}
                    </span>
                  </span>
                  <span className='text-slate-400'>→</span>
                  <span className='font-semibold font-mono'>
                    {truncateText(interactionItem.details?.call_number || 'N/A')}
                  </span>
                </>
              ) : (
                <>
                  <span className='font-semibold font-mono'>
                    {truncateText(interactionItem.details?.call_number || 'N/A')}
                  </span>
                  <span className='text-slate-400'>→</span>
                  <span className='font-semibold'>
                    {interactionItem.details?.aircall_phone_name && (
                      <span className='mr-1'>{truncateText(interactionItem.details.aircall_phone_name)}</span>
                    )}
                    <span className='font-mono'>
                      {truncateText(interactionItem.details?.aircall_phone_number || 'N/A')}
                    </span>
                  </span>
                </>
              )}
            </div>
          )}

          {/* Línea nuestra por la que salió el WhatsApp. Va el número y no solo el
              nombre: hay líneas distintas que se llaman igual («Redmi1»). */}
          {interactionItem.type === 'whatsapp_sent' && (
            <div className='px-2.5 py-0.5 mt-2 bg-slate-50/80 border border-slate-200 rounded-lg flex items-center gap-1.5 text-xs text-slate-600 shrink-0'>
              <span className='text-slate-400'>Desde</span>
              <span className='font-semibold'>
                {esAdmin && interactionItem.sending_line?.name && (
                  <span className='mr-1'>{truncateText(interactionItem.sending_line.name)}</span>
                )}
                <span className='font-mono'>{truncateText(interactionItem.sending_line?.phone_number || 'N/A')}</span>
              </span>
            </div>
          )}

          {/* Message if exists */}
          {interactionItem.details?.message && (
            <p className='mt-2 text-sm text-slate-700 leading-relaxed'>{interactionItem.details.message}</p>
          )}

          {/* Email content - collapsible */}
          {isEmail && (interactionItem.details?.subject || interactionItem.details?.body_html || interactionItem.details?.body_text) && (
            <div className='mt-2'>
              <div className='flex items-center gap-2 text-xs text-slate-500'>
                {interactionItem.details?.from && (
                  <span>{interactionItem.direction === 'outgoing' ? 'Para:' : 'De:'} {interactionItem.details.from_name || interactionItem.details.from}</span>
                )}
                {interactionItem.details?.subject && (
                  <>
                    <span className='text-slate-300'>|</span>
                    <span className='truncate font-medium text-slate-600'>{interactionItem.details.subject}</span>
                  </>
                )}
              </div>
              <button
                type='button'
                onClick={() => setIsEmailExpanded(!isEmailExpanded)}
                className='mt-1 inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 font-medium'
              >
                {isEmailExpanded ? (
                  <>
                    Ocultar contenido
                    <ChevronUpIcon className='w-3 h-3' />
                  </>
                ) : (
                  <>
                    Ver contenido
                    <ChevronDownIcon className='w-3 h-3' />
                  </>
                )}
              </button>
              {isEmailExpanded && (
                <div className='mt-2 p-3 bg-white rounded-lg border border-slate-200 text-sm'>
                  {interactionItem.details?.body_html ? (
                    <div
                      className='prose prose-sm max-w-none text-slate-700 [&_a]:text-blue-600 [&_a]:underline'
                      dangerouslySetInnerHTML={{ __html: sanitizeEmailHtml(interactionItem.details.body_html) }}
                    />
                  ) : (
                    <p className='text-slate-700 whitespace-pre-wrap'>
                      {interactionItem.details?.body_text || interactionItem.details?.body || '(Sin contenido)'}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Info de la llamada: duración y grabación */}
          {isCall && (duration || hasRecording || canFetchRecording) && (
            <div className='mt-3 flex items-center gap-3 flex-wrap'>
              {duration && (
                <div className='inline-flex items-center gap-1.5 text-xs text-slate-600 bg-white px-2.5 py-1.5 rounded-lg border border-slate-200'>
                  <ClockIcon className='w-3.5 h-3.5 text-slate-400' />
                  <span className='font-medium'>{duration}</span>
                </div>
              )}
              {hasRecording && (
                <button
                  onClick={handlePlayRecording}
                  disabled={isLoadingRecording}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors shadow-sm ${
                    isLoadingRecording
                      ? 'text-white bg-gray-400 cursor-not-allowed'
                      : 'text-white bg-blue-600 hover:bg-blue-700'
                  }`}
                  title='Escuchar grabación'
                >
                  <PlayIcon className='w-3.5 h-3.5' />
                  {isLoadingRecording ? 'Cargando...' : 'Escuchar grabación'}
                </button>
              )}
              {canFetchRecording && (
                <button
                  onClick={handleGetAircallRecording}
                  disabled={isFetchingRecording}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors shadow-sm ${
                    isFetchingRecording
                      ? 'text-white bg-gray-400 cursor-not-allowed'
                      : 'text-white bg-green-600 hover:bg-green-700'
                  }`}
                  title='Obtener grabación de Aircall'
                >
                  <ArrowDownTrayIcon className='w-3.5 h-3.5' />
                  {isFetchingRecording ? 'Obteniendo...' : 'Obtener de Aircall'}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
});

export default InteractionItem;
